import {
  Component,
  h,
  Prop,
  Watch,
  Element,
  Listen,
  Event,
  EventEmitter,
  Host,
  State,
} from '@stencil/core';
import store, { Filter, SuggestionGroup } from './store';
import { responsive } from '../../responsive/responsive';
import { IntergatorConnector } from '../../common/intergator-connector';
import {
  quickfilters,
  getSpecialQuickfilter,
} from './ui-download-search-quickfilters/filters';

@Component({
  tag: 'ui-download-search',
  styleUrl: 'ui-download-search.scss',
  shadow: false,
})
export class UiDownloadSearch {
  @Element() el: HTMLElement;

  @Prop() apiToken: string;

  @Prop() searchApi: string;

  @Prop() facetsApi: string;

  @Prop() suggestionsApi: string;

  @Prop({ reflect: true }) componentWidth: number;

  @Prop() thumbnailBaseUrl: string;

  @Prop() headline: string;

  @Prop() resultsPerPage: number;

  @Prop() documentsInformationApi: string;

  @State() isApiConnectorsReady = false;

  @State() isUrlParamsReady = false;

  @State() isFundsBoxReady = true;

  @Event({ eventName: 'uiSearchesPaginationChange' })
  paginationChangeEvent: EventEmitter;

  facetsApiConnector: IntergatorConnector;

  resultsApiConnector: IntergatorConnector;

  suggestionsApiConnector: IntergatorConnector;

  componentWillLoad() {
    this.renameUrlSearchParams();
    store.state.tracking.caller = this.el;
    store.state.thumbnailBaseUrl = this.thumbnailBaseUrl;
    this.setApiConnectors();
    // Note: Do not listen on 'offset' – it's handled via the pagination listener!

    store.onChange('currentQuickfilter', () => {
      this.setUrlSearchParams();
    });
  }

  componentDidLoad() {
    responsive(this.el);
  }

  @Watch('facetsApi')
  @Watch('searchApi')
  @Watch('suggestionsApi')
  @Watch('apiToken')
  apiLoaded() {
    this.setApiConnectors();
  }

  @Watch('resultsPerPage')
  onPagination() {
    this.setPagination();
  }

  @Watch('isApiConnectorsReady')
  @Watch('isFundsBoxReady')
  @Watch('isUrlParamsReady')
  onReadyToFetch() {
    if (
      this.isApiConnectorsReady &&
      this.isUrlParamsReady &&
      this.isFundsBoxReady
    ) {
      this.addStoreWatchers();
      this.fetchFacets();
      this.fetchResults();
    }
  }

  private addStoreWatchers() {
    store.onChange('selectedFilters', () => {
      this.selectedFiltersChanged.emit(store.state.selectedFilters);
      this.setFundsModeBasedOnActiveQuickfilter();
      this.fetchFacets();
      this.resetPagination();
      this.fetchResults();
      this.setUrlSearchParams();
    });
    store.onChange('searchValue', () => {
      this.fetchFacets();
      this.resetPagination();
      this.fetchResults();
      this.setUrlSearchParams();
    });
    store.onChange('selectedFunds', () => {
      this.resetPagination();
      this.fetchResults();
      this.setUrlSearchParams();
    });
    store.onChange('sorting', () => {
      this.resetPagination();
      this.fetchResults();
    });
    store.onChange('resultLimit', () => {
      this.resetPagination();
      this.fetchResults();
    });
  }

  setFundsModeBasedOnActiveQuickfilter() {
    store.state.fundsMode =
      store.state.currentQuickfilter?.label === quickfilters.FUNDS.label;
  }

  /**
   * Convert old param names to new names.
   * We might get old names due to content links not being updated yet.
   */
  private renameUrlSearchParams() {
    const params = new URLSearchParams(window.location.search);

    // list of old and new param names
    const paramNames = [
      { old: 'query', new: 'q' },
      { old: 'obj_fonds', new: 'fonds' },
      { old: 'obj_isin', new: 'isin' },
      { old: 'obj_themen', new: 'topic' },
      { old: 'obj_materialien', new: 'type' },
    ];

    // for each old name found, replace with new name
    paramNames.forEach((i) => {
      if (params.has(i.old)) {
        params.set(i.new, params.get(i.old));
        params.delete(i.old);
      }
    });

    // also normalize old filter value 'OnlyVKU' to 'vku'
    if (params.has('filter') && params.get('filter') === 'OnlyVKU') {
      params.set('filter', 'vku');
    }

    if (store.state.currentQuickfilter?.urlValue) {
      params.set('filter', store.state.currentQuickfilter.urlValue);
    }

    window.history.replaceState(
      null,
      null,
      `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`
    );
  }

  /**
   * read url params and update store values
   */
  private async getUrlSearchParams() {
    const urlParams: any = new Proxy(
      new URLSearchParams(window.location.search),
      {
        get: (searchParams, prop) => searchParams.get(prop.toString()),
      }
    );

    if (urlParams.filter) {
      Object.values(quickfilters).forEach((el) => {
        if (urlParams.filter === el.urlValue) {
          store.state.selectedFilters = el.filters;
          const filter = getSpecialQuickfilter('label', el.label);
          store.state.currentQuickfilter = filter;
        }
      });
    }

    if (urlParams.filter === quickfilters.FUNDS.urlValue) {
      store.state.fundsMode = true;
    }

    if (store.state.fundsMode) {
      // get params ISIN and/or FUND from url params
      const paramIsin = urlParams.isin;
      const paramFund = urlParams.fonds;

      // ℹ if both are truthy, ISIN shall take priority, as it is unique, thus reliable,
      // and selecting a fund by name should only be used as a fallback.
      const keyword = paramIsin || paramFund;
      if (keyword) {
        await this.populateFundsBox(keyword);
      }
    }

    if (!store.state.fundsMode) {
      const qParams = [];

      if (urlParams.q) {
        qParams.push(`${urlParams.q}`);
      }
      if (urlParams.fonds) {
        qParams.push(`fonds:${urlParams.fonds}`);
      }
      if (urlParams.isin) {
        qParams.push(`isin:${urlParams.isin}`);
      }
      if (urlParams.topic) {
        qParams.push(`topic:${urlParams.topic}`);
      }
      if (urlParams.type) {
        qParams.push(`type:${urlParams.type}`);
      }

      const logical = urlParams.logical
        ? urlParams.logical.toUpperCase()
        : 'AND';

      store.state.searchValue = qParams.join(` ${logical} `);
    }
    this.isUrlParamsReady = true;

    this.setUrlSearchParams();
  }

  /**
   * set url params based on store values
   */
  private setUrlSearchParams() {
    const params = new URLSearchParams();
    if (
      store.state.currentQuickfilter &&
      store.state.currentQuickfilter.urlValue
    ) {
      params.set('filter', store.state.currentQuickfilter.urlValue);
    }
    if (store.state.fundsMode) {
      // Even if loading search with `?filter=vku&fonds=myfund` only reflect ISIN
      // back to url params as it's unique, thus more reliable, than a fund name
      const isin = store.state.selectedFunds[0]?.isin;
      if (isin) params.set('isin', isin);
    }

    if (!store.state.fundsMode) {
      if (store.state.searchValue) params.set('q', store.state.searchValue);
    }

    // TODO: persist other params, e.g. searchWebComponentsVersion
    window.history.replaceState(
      null,
      null,
      `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`
    );
  }

  private setPagination() {
    if (this.resultsPerPage) {
      store.state.resultLimit = Number(this.resultsPerPage);
    }
  }

  private resetPagination() {
    if (store.state.offset !== 0) {
      store.state.offset = 0;
      this.paginationChangeEvent.emit();
    }
  }

  /**
   * Fetch suggestions for `keyword`, then
   * push results to `selectedFunds` to trigger display of `fundsBox`
   */
  private async populateFundsBox(keyword: string) {
    this.isFundsBoxReady = false;
    const suggestionGroups = await this.fetchSuggestions(keyword);
    if (suggestionGroups?.length > 0) {
      store.state.selectedFunds = [...suggestionGroups[0].hits];
    } else {
      console.debug(
        `No suggestions found for '${keyword}', nothing to show in funds-box.`
      );
    }
    this.isFundsBoxReady = true;
  }

  /**
   * Set up IntergatorConnectors for facets, results, suggestions,
   * and then fetch results & facets.
   */
  private setApiConnectors() {
    if (
      this.facetsApi &&
      this.searchApi &&
      this.apiToken &&
      this.suggestionsApi &&
      !this.isApiConnectorsReady
    ) {
      this.facetsApiConnector = new IntergatorConnector({
        url: this.facetsApi,
        key: this.apiToken,
        cache: true,
      });
      this.resultsApiConnector = new IntergatorConnector({
        url: this.searchApi,
        key: this.apiToken,
        cache: true,
      });
      this.suggestionsApiConnector = new IntergatorConnector({
        url: this.suggestionsApi,
        key: this.apiToken,
      });

      this.isApiConnectorsReady = true;

      this.setPagination();
      this.getUrlSearchParams();
    }
  }

  // this handles the offset
  @Listen('fetchResults')
  onPaginationChange(ev) {
    // The following checks if event came from inside the download search
    if (!this.el.contains(ev.target)) {
      return;
    }
    store.state.offset = ev.detail.from;
    store.state.tracking.trackAction('DownloadSearchPaginationClick');
    this.fetchResults();
  }

  @Watch('componentWidth')
  onComponentWidthChange() {
    store.state.isMobile = this.componentWidth < 1024;
  }

  @Event() selectedFiltersChanged: EventEmitter<Filter[]>;

  private getDownloadSearchFilters() {
    return IntergatorConnector.getDownloadSearchFilters(
      store.state.fundsMode,
      store.state.selectedFilters.map((filter) => filter.value)
    );
  }

  /**
   * Get detailed funds data for `keyword` from funds suggestion API
   *
   * @param keyword isin or funds name
   */
  private async fetchSuggestions(keyword: string) {
    await this.suggestionsApiConnector;
    const request = await this.suggestionsApiConnector.fetchSuggestions({
      keyword,
      filters: IntergatorConnector.getDownloadSearchSuggestionFilters(true),
    });

    if (request.ok) {
      const suggestionGroups = (await request.clone().json()).results;
      return suggestionGroups as SuggestionGroup[];
    }
    return null;
  }

  private async fetchFacets() {
    if (store.state.fundsMode) {
      return;
    }

    const request = await this.facetsApiConnector.fetchFacets({
      q: IntergatorConnector.convertQForDownloadSearch(store.state.searchValue),
      filters: this.getDownloadSearchFilters(),
    });

    if (request.ok) {
      store.state.facets = IntergatorConnector.convertFacets(
        (await request.clone().json()).results
      );
    }
  }

  private async fetchResults() {
    store.state.isFetchingResults = true;
    const request = await this.resultsApiConnector.fetchResults({
      q: !store.state.fundsMode
        ? IntergatorConnector.convertQForDownloadSearch(store.state.searchValue)
        : IntergatorConnector.convertFundsQForDownloadSearch(
            store.state.selectedFunds.map((fund) => fund.isin)
          ),
      filters: this.getDownloadSearchFilters(),
      of: store.state.offset,
      si: store.state.resultLimit,
      od: store.state.sorting,
    });

    if (request.ok) {
      const results = (await request.clone().json()).results[0];
      store.state.totalResults = results.matchingDocumentCount;

      store.state.results = store.state.fundsMode
        ? IntergatorConnector.convertFunds(results)
        : IntergatorConnector.convertDownloads(results);
      store.state.isFetchingResults = false;
      store.state.tracking.trackFetch('DownloadSearchFetchResults');
    }
  }

  render() {
    return (
      <Host>
        <ui-headline level={2}>{this.headline}</ui-headline>
        <ui-container
          class="quickfilter-container"
          variant="secondary100"
          fullWidth={true}
        >
          <ui-download-search-quickfilters>
            <slot></slot>
          </ui-download-search-quickfilters>
          {store.state.fundsMode && (
            <div class="fundsText">
              Erstellen Sie eine Auswahl an Fonds zu denen Sie Dokumente
              herunterladen möchten. Sie können eine beliebige Anzahl an Fonds
              zu Ihrer Auswahl hinzufügen.
            </div>
          )}
          <ui-download-search-autosuggest
            suggestionsApi={this.suggestionsApi}
            apiKey={this.apiToken}
            placeholder={
              store.state.fundsMode
                ? window.ui.services?.i18n?.translate(
                    'downloadSearchFundSearchFor'
                  ) || 'Suche nach Fondsname, ISIN oder WKN'
                : `Suche in ${
                    store.state.currentQuickfilter?.label
                      ? `„${store.state.currentQuickfilter.label}”`
                      : 'allen Dokumenten'
                  }`
            }
          />
        </ui-container>
        {store.state.fundsMode && store.state.selectedFunds.length > 0 && (
          <ui-download-search-funds-box></ui-download-search-funds-box>
        )}
        <ui-download-search-facets></ui-download-search-facets>
        <ui-download-search-selectable-results
          downloadMaterialApi={this.documentsInformationApi}
          apiKey={this.apiToken}
        ></ui-download-search-selectable-results>
        {store.state.totalResults > store.state.resultLimit && (
          <ui-search-results-pagination
            results={store.state.totalResults}
            resultsPerPage={store.state.resultLimit}
            scrollIntoElementSelector="ui-download-search"
          ></ui-search-results-pagination>
        )}
      </Host>
    );
  }
}
